import {
  AwaitContainer,
  Card,
  CardContent,
  Menu,
  PlayPauseButton,
  RepeatButton,
  SeekVisualizer,
  Skeleton,
} from '@ibsys/kit';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { RecordingUnavailableNotice, useConversationTranscript, useDialogAudioState } from '@/entities/conversations';
import { useDialogSessionOptional } from '@/features/DialogSession';
import { cn } from '@/shared/lib';
import { EditedIntervalHighlight } from './EditedIntervalHighlight';
import { InterruptionIntervals } from './InterruptionIntervals';
import { PauseIntervals } from './PauseIntervals';
import { SpeakerIntervals } from '@/pages/dialogs/DialogAudioSection/SpeakerIntervals';

/**
 * Высота области записи — высота шкалы записи. Её держат все состояния области (заглушка,
 * ожидание, «недоступна»), чтобы смена состояния не сдвигала разметку под карточкой.
 */
const RECORDING_AREA_HEIGHT = 75;

export const DialogAudioSection: FC = () => {
  const { id } = useParams<{ id?: string }>();
  const dialogSession = useDialogSessionOptional();
  const transcriptQuery = useConversationTranscript(id || '');

  const transcriptData = dialogSession ? dialogSession.transcriptsQuery.data : (transcriptQuery.data ?? []);
  const isLoadingTranscript = dialogSession ? dialogSession.transcriptsQuery.isLoading : transcriptQuery.isLoading;
  const audioDuration = dialogSession?.duration ?? 0;

  // Внутри сессии диалога состояние записи уже ведёт она: своё ожидание записи здесь было бы вторым
  const { status: ownAudioStatus, retryRecordingFetch: ownRetryRecordingFetch } = useDialogAudioState(
    dialogSession ? undefined : id,
  );
  const audioStatus = dialogSession?.audioStatus ?? ownAudioStatus;
  const retryRecordingFetch = dialogSession?.retryRecordingFetch ?? ownRetryRecordingFetch;

  // Записи нет или получить её не удалось — сразу «недоступна», без индикатора. Неудачу получения
  // у источника можно повторить.
  if (audioStatus === 'unavailable' || audioStatus === 'failed') {
    return (
      <div className={'p-1'}>
        <Card className={'w-full py-3'}>
          <CardContent className={'py-0 flex items-center'} style={{ minHeight: RECORDING_AREA_HEIGHT }}>
            <RecordingUnavailableNotice onRetry={audioStatus === 'failed' ? retryRecordingFetch : undefined} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Состояние записи ещё неизвестно (карточка диалога грузится) — зарезервированное место без
  // индикатора, чтобы не было дёрганья индикатор → «недоступна» у диалогов без записи.
  if (audioStatus === 'loading') {
    return (
      <div className={'p-1 overflow-hidden'}>
        <Card className={'w-full py-3'}>
          <CardContent className={'py-0'}>
            <Skeleton className={'w-full'} style={{ height: RECORDING_AREA_HEIGHT }} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Запись готова или ожидается — карточка плеера в зарезервированном месте; пока ссылка
  // не готова (pending), показываем индикатор поверх. Шкала без источника записи не рисуется
  // вовсе, поэтому её место держится высотой области — иначе плеер сдвинул бы разметку вниз.
  return (
    <div className={'p-1 overflow-hidden'}>
      <Card className={'w-full py-3 '}>
        <AwaitContainer isLoading={audioStatus === 'pending' || isLoadingTranscript} className="rounded-xl">
          <CardContent className={'py-0'}>
            <div className={cn('flex items-center gap-2')} data-size={'md'}>
              <PlayPauseButton />
              <RepeatButton />
              <div className={'flex-1 relative w-full min-w-0'} style={{ minHeight: RECORDING_AREA_HEIGHT }}>
                <SeekVisualizer
                  height={RECORDING_AREA_HEIGHT}
                  className={'w-full'}
                  barColor={'var(--color-primary)'}
                  barPlayedColor={'var(--color-primary-hover)'}
                />
                <div className={'absolute inset-0 left-12 right-12 h-full pointer-events-none'}>
                  <SpeakerIntervals transcriptData={transcriptData} audioDuration={audioDuration} />
                  <PauseIntervals transcriptData={transcriptData} audioDuration={audioDuration} />
                  <InterruptionIntervals transcriptData={transcriptData} audioDuration={audioDuration} />
                  <EditedIntervalHighlight audioDuration={audioDuration} />
                </div>
              </div>
              <Menu />
            </div>
          </CardContent>
        </AwaitContainer>
      </Card>
    </div>
  );
};
